import type { PlaceholderDataFunction } from '@tanstack/react-query'
import { API_BASE_URL } from '../../constants/system'
import { SKY_SOCIAL_CUSTOMER, SKY_SOCIAL_JOB } from '../../constants/skySocialMedia'

/**
 * The one place a Sky product URL is built.
 *
 * WHY THIS IS A MODULE AND NOT A TEMPLATE STRING IN EACH HOOK. The whole point
 * of the `/sky-social-media` route group is that customer and job appear in no
 * URL and no request body — the backend fixes both (`ForceRouteScope`) and would
 * ignore ours. A hook that hand-rolls its path is one refactor away from
 * reintroducing `/autocoder/${customer}/${job}/…`, which would still WORK (the
 * old routes are live) while silently bypassing `skyProductScope` and therefore
 * the product's campaign filter. That failure renders one department's totals
 * under another's heading with a 200 and no error — the same class of bug the
 * forced `driver: platinum` exists to prevent.
 *
 * So: every socialdash hook goes through here, and none of them takes a
 * customer or a job argument at all. There is nothing to pass and nothing to
 * get wrong.
 */
export function skyProductURL(product: string, path: string): string {
  const slug = encodeURIComponent(product.trim().toLowerCase())
  const suffix = path.startsWith('/') ? path : `/${path}`
  return `${API_BASE_URL}/sky-social-media/${slug}${suffix}`
}

/**
 * The JOB-WIDE mount, for the handful of Sky screens that are not about one
 * department (#1196's post-data editor).
 *
 * 🔴 THIS IS THE EXCEPTION THE WARNING ABOVE ANTICIPATES, AND IT IS NARROW.
 * The hazard named there is real: a DASHBOARD hook that hand-rolls this path
 * still returns 200 while silently bypassing `skyProductScope`, putting one
 * department's numbers under another's heading. It does not reach the editor,
 * for a reason worth writing down rather than assuming — `/entities/query`,
 * `GET /entities/:id` and its PATCH are registered ONLY on the job-wide group
 * (`RegisterAutoCoderRoutes`) and are NOT mounted on the product group at all.
 * There is no product-scoped version to bypass; a request through a product
 * slug would 404.
 *
 * That is not an oversight either. Correcting a post's numbers is a job-wide
 * repair: you paste a link and fix what it says, and which department the post
 * belongs to is an ANSWER the page gives you, not a precondition you have to
 * supply first.
 *
 * ⚠ Anything that renders a NUMBER FOR A DEPARTMENT belongs on skyProductURL,
 * always. If a panel here ever starts aggregating, it moves.
 */
export function skyJobURL(path: string): string {
  const suffix = path.startsWith('/') ? path : `/${path}`
  return `${API_BASE_URL}/autocoder/${SKY_SOCIAL_CUSTOMER}/${SKY_SOCIAL_JOB}${suffix}`
}

/**
 * React-query key prefix for one product's data.
 *
 * Keyed on the product slug so switching departments is a cache miss rather
 * than a flash of the previous department's numbers — which, on a dashboard
 * whose entire purpose is per-department separation, would be the most
 * misleading possible transition.
 */
export function skyQueryKey(product: string, name: string, extra?: Record<string, unknown>) {
  return ['sky_social', name, { product, ...(extra ?? {}) }] as const
}

/**
 * `placeholderData` for a Sky dashboard query: keep the previous answer on
 * screen while a FILTER change refetches, and never across anything else.
 *
 * A filter change re-keys every query, which used to blank the whole page for
 * the two to five seconds the new answers take (speed review, 16 Oct 2026).
 * Showing the previous numbers, dimmed, until the new ones land is calmer and
 * hides nothing.
 *
 * 🔴 ONLY THE FILTER MAY DIFFER. The previous data is kept when the previous
 * key equals this one in everything but the filter part of `labelsKey`: same
 * product, same query, same chart batch, same tag, and the same SCOPE inside
 * the filter — the comment and published date ranges and the source
 * (`labelsScope`). Last period's figures drawn under this period's caption
 * would be attributed to the wrong months. A department switch must still be a cache miss
 * (see skyQueryKey above), and so must the output-share tag, whose previous
 * pie would otherwise be drawn under the new tag's name.
 */
export function keepWhileOnlyFilterChanges<T, K extends readonly unknown[] = readonly unknown[]>(
  queryKey: K,
): PlaceholderDataFunction<T, Error, T, K> {
  const identity = viewIdentity(queryKey)
  return (previous, previousQuery) =>
    previousQuery && viewIdentity(previousQuery.queryKey) === identity ? previous : undefined
}

/** A Sky query key without its filter: what the panel IS, not what it is filtered by. */
function viewIdentity(queryKey: readonly unknown[]): string {
  const [root, name, extra] = queryKey
  const rest: Record<string, unknown> = { ...((extra as Record<string, unknown> | undefined) ?? {}) }
  const scope = typeof rest.labelsKey === 'string' ? labelsScope(rest.labelsKey) : ''
  delete rest.labelsKey
  const sorted = Object.keys(rest).sort().map((k) => [k, rest[k]])
  return JSON.stringify([root, name, sorted, scope])
}

/**
 * The parts of a `labelsKey` that say WHICH numbers these are rather than
 * narrowing them: the comment date range, the posts' published date range and
 * the source. A change to any of them must blank, not keep, the previous data.
 */
export function labelsScope(labelsKey: string): string {
  let q: Record<string, any> | null = null
  try {
    q = JSON.parse(labelsKey)
  } catch {
    return labelsKey
  }
  if (!q || typeof q !== 'object') return ''
  return JSON.stringify([
    q.commentDateRange ?? null,
    q.entity?.publishedDateRange ?? null,
    q.metadataAny?.source ?? null,
  ])
}
